import { clients, HttpError } from 'lib/https';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { ConfigurationSettings } from '/models/configuration-settings';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import { getAccountWithAccessToken } from 'features/profile/services/accounts-service';
import { UserMembership } from 'types/user-type';

export interface SignInResponse {
  access_token?: string;
  refresh_token?: string;
  enable_mfa?: boolean;
  mfaId?: string;
  mfaType?: number;
  token_type?: string;
  expires_in?: number;
  scope?: string | null;
  id_token?: string | null;
}

interface AccountActivationData {
  firstName: string;
  lastName: string;
  password: string;
  code: string;
}

interface AccountActivationPayload extends AccountActivationData {
  ProjectKey: string;
  preventPostEvent: boolean;
}

export type PasswordSigninPayload = {
  grantType: 'password';
  username: string;
  password: string;
  captcha_code?: string;
  termsAndConditionsAccepted?: boolean;
};

export type SSoSigninPayload = {
  grantType: 'social';
  code: string;
  state: string;
  provider: string;
};

export type OidcSigninPayload = {
  grantType: 'authorization_code';
  code: string;
};

export type MFASigninPayload = {
  grantType: 'mfa_code';
  code: string;
  mfaId: string;
  mfaType: number;
};

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

export type CompleteSigninResponse = {
  accessToken: string;
  refreshToken: string;
  organizationId: string | null;
};

export type ISignupPayload = {
  email: string;
  firstName: string;
  lastName: string;
  captchaCode: string;
  terms: boolean;
};

export interface ISignupResponse {
  itemId: string | null;
  errors: unknown | null;
  isSuccess: boolean;
}

export interface SigninBySSOPayload {
  grantType: 'social';
  code: string;
  state: string;
  provider: string;
}

export interface SigninByOidcPayload {
  grantType: 'authorization_code';
  code: string;
}

export const signin = async <
  T extends 'password' | 'social' | 'mfa_code' | 'authorization_code' = 'password'
>(
  payload: PasswordSigninPayload | MFASigninPayload | SigninBySSOPayload | SigninByOidcPayload,
  config: ConfigurationSettings
): Promise<T extends 'password' ? SignInResponse : MFASigninResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.login}`;

  // sign in flow
  if (payload.grantType === 'password') {    
    const requestBody: any = {
      username: payload.username,
      password: payload.password,
    };
    
    if (payload.captcha_code) {
      requestBody.captcha_code = payload.captcha_code;
    }

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    const result = await response.json();

    if (result?.error === 'mfa_enabled' || result?.mfa_required) {
      throw new HttpError(response.status, result);
    }

    if (!response.ok) {
      trackEvent(EVENTS.ERROR(result, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, result);
    }
    
    // Transform new response format to match existing format
    // The new API returns: { token_type, expires_in, scope, id_token }
    // We need to map id_token to both access_token and refresh_token for compatibility
    return {
      access_token: result.id_token || '',
      refresh_token: result.id_token || '',
      enable_mfa: result.enable_mfa || result.mfa_required || false,
      mfaId: result.mfaId || result.mfa_id || '',
      mfaType: result.mfaType ?? result.mfa_type ?? 0,
      token_type: result.token_type,
      expires_in: result.expires_in,
      scope: result.scope,
      id_token: result.id_token,
    } as any;
  } else if (payload.grantType === 'social') {
    const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || process.env.REACT_APP_PUBLIC_OIDC_CLIENT_ID || '';
    /* const signinBySSOData = new URLSearchParams();
    signinBySSOData.append('client_id', clientId);
    signinBySSOData.append('code', payload.code);
    signinBySSOData.append('state', payload.state); */

    const signinBySSOData = {
      client_id: clientId,
      code: payload.code,
      state: payload.state,
      provider: payload.provider
    };
    

    const response = await fetch(`${baseUrl}${config.paths.authentication.socialLogin}`, {
      method: 'POST',
      body: JSON.stringify(signinBySSOData),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else if (payload.grantType === 'authorization_code') {
    const oidcData = new URLSearchParams();
    oidcData.append('grant_type', 'authorization_code');
    oidcData.append('code', payload.code);

    const response = await fetch(url, {
      method: 'POST',
      body: oidcData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else {
    // MFA OTP Verification flow
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        mfa_type: payload.mfaType,
        mfa_code: payload.code || '',
        mfa_id: payload.mfaId,
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.login
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  }
};

export const signout = async (
  config: ConfigurationSettings
): Promise<{
  isSuccess: true;
}> => {
  try {
    localStorage.removeItem('auth-storage');
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.authentication.logout}`;
    return await clients.post(
      url,
      config.blocksKey,
      JSON.stringify({
        refreshToken: useAuthStore.getState().refreshToken,
      })
    );
  } catch (error) {
    console.error('Logout operation failed:', error);
    throw error;
  }
};

let refreshInFlight: Promise<any> | null = null;

export const getRefreshToken = (config: ConfigurationSettings) => {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    // The refresh grant lives on its own JSON-only endpoint; the rt_ cookie is
    // the primary credential, so an empty body token is still refreshable.
    const url = `${baseUrl}${config.paths.authentication.refreshToken}`;

    const response = await fetch(url, {
      method: 'POST',
      // Empty body token matches the https.ts interceptor: the rt_ cookie is
      // the credential, and a stale store value must not override it.
      body: JSON.stringify({ refresh_token: '' }),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    const result = await response.json();

    if (!response.ok) {
      trackEvent(EVENTS.ERROR(result, {
        endpoint: config.paths.authentication.refreshToken
      }))
      throw new HttpError(response.status, result);
    }

    return result;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

export const accountActivation = async (
  data: {
    firstName: string;
    lastName: string;
    password: string;
    code: string;
    captchaCode: string;
  },
  config: ConfigurationSettings
) => {
  const payload: AccountActivationPayload = {
    ...data,
    ProjectKey: config.blocksKey,
    preventPostEvent: true
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.activateAccount}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const forgotPassword = async (
  data: {
    email: string;
    captchaCode?: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    mailPurpose: 'RecoverAccount',
    ProjectKey: config.blocksKey,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.recoverAccount}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const resetPassword = async (
  data: { code: string; password: string; captchaCode: string },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    logoutFromAllDevices: true,
    ProjectKey: config.blocksKey,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.resetPassword}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const resendActivation = async (
  data: {
    userId: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    projectKey: config.blocksKey,
    ...data,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.resendActivation}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const logoutAll = async (config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.logoutAll}`;
  return clients.post(url, config.blocksKey, '');
};

export const switchOrganization = async (
  orgId: string,
  config: ConfigurationSettings,
  accessToken?: string
): Promise<MFASigninResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.switchOrganization}`;
  const token = accessToken ?? useAuthStore.getState().accessToken;

  return clients.post(
    url,
    config.blocksKey,
    JSON.stringify({ organization_id: orgId }),
    token ? { Authorization: `bearer ${token}` } : undefined
  );
};

const resolveOrganizationId = (memberships?: UserMembership[]) => {
  if (!memberships?.length) {
    return null;
  }

  return memberships.find((membership) => membership.organizationId !== 'default')?.organizationId
    ?? memberships[0]?.organizationId
    ?? null;
};

export const completeSigninWithOrganization = async (
  auth: Pick<MFASigninResponse, 'access_token' | 'refresh_token'>,
  config: ConfigurationSettings
): Promise<CompleteSigninResponse> => {
  const account = await getAccountWithAccessToken(config, auth.access_token);
  const organizationId = resolveOrganizationId(account.memberships);

  if (!organizationId) {
    return {
      accessToken: auth.access_token,
      refreshToken: auth.refresh_token,
      organizationId: null,
    };
  }

  const switchedAuth = await switchOrganization(organizationId, config, auth.access_token);

  return {
    accessToken: switchedAuth.access_token,
    refreshToken: switchedAuth.refresh_token ?? auth.refresh_token,
    organizationId,
  };
};

export const signup = async (
  payload: ISignupPayload,
  config: ConfigurationSettings
): Promise<ISignupResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.identifier.signup}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-blocks-key': config.blocksKey,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new HttpError(response.status, err);
  }

  return response.json();
};

/**
 * Generic function to refresh access token using refresh token
 * Can be called from anywhere in the application
 * @param config - Configuration settings containing API endpoints and blocks key
 * @returns Promise with new access token and refresh token
 * @throws HttpError if refresh token is invalid or expired
 */
export const refreshAccessToken = async (config: ConfigurationSettings) => {
  const authStore = useAuthStore.getState();

  try {
    const refreshTokenRes = await getRefreshToken(config);

    if (refreshTokenRes.error) {
      throw new HttpError(401, refreshTokenRes);
    }

    // Cookie-mode responses may renew the session without body tokens; only
    // touch the store when tokens are actually present.
    if (refreshTokenRes.access_token) {
      authStore.setAccessToken(refreshTokenRes.access_token);
    }
    if (refreshTokenRes.refresh_token) {
      authStore.setRefreshToken(refreshTokenRes.refresh_token);
    }

    return refreshTokenRes;
  } catch (error) {
    // If it's already an HttpError, re-throw it
    if (error instanceof HttpError) {
      throw error;
    }
    
    // For other errors, wrap them in an HttpError
    console.error('Failed to refresh access token:', error);
    throw new HttpError(500, { error: 'Failed to refresh access token' });
  }
};
